import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import Cookies from 'js-cookie';
import Container from './index';
import ChatSidebar from '../ChatSidebar';
import HelpSupportModal from '../HelpSupportModal';
import CreateCaseModal from '../CreateCaseModal';
import SettingsModal from '../SettingsModal';
import SuccessModal from '../SuccessModal';
import FAQModal from '../FAQModal';
import type { ReactNode } from 'react';
import type { AuthModalMode, AuthModalState } from '../../types/auth';

const AUTH_BASE_URL =
    window.location.hostname === 'localhost' ? 'http://localhost:3000' : 'https://auth.podus.org';

interface AppShellContextValue {
    countries: string[];
    selectedCountry: string;
    setSelectedCountry: (country: string) => void;
    isLoggedIn: boolean;
    setIsLoggedIn: (value: boolean) => void;
    authModal: AuthModalState;
    openAuthModal: (mode: AuthModalMode) => void;
    closeAuthModal: () => void;
    changeAuthModalMode: (mode: AuthModalMode) => void;
    isSidebarOpen: boolean;
    setIsSidebarOpen: (value: boolean) => void;
    toggleSidebar: () => void;
    isSidebarCollapsed: boolean;
    toggleSidebarCollapse: () => void;
    isHelpModalOpen: boolean;
    openHelpModal: () => void;
    closeHelpModal: () => void;
    isCreateCaseModalOpen: boolean;
    openCreateCaseModal: () => void;
    closeCreateCaseModal: () => void;
    isSettingsModalOpen: boolean;
    openSettingsModal: () => void;
    closeSettingsModal: () => void;
    isFAQModalOpen: boolean;
    openFAQModal: () => void;
    closeFAQModal: () => void;
    showCaseSuccessModal: boolean;
    openCaseSuccessModal: () => void;
    closeCaseSuccessModal: () => void;
    // Sidebar refresh
    sidebarRefreshTrigger: number;
    triggerSidebarRefresh: () => void;
}

interface AppShellProps {
    children: ReactNode;
    disableSidebar?: boolean;
    contentClassName?: string;
    contentId?: string;
    onLoginSuccess?: () => void;
}

const DEFAULT_COUNTRIES = ['NG', 'GB'];

const AppShellContext = createContext<AppShellContextValue | undefined>(undefined);

export function useAppShell(): AppShellContextValue {
    const context = useContext(AppShellContext);
    if (!context) {
        throw new Error('useAppShell must be used within an AppShell component.');
    }

    return context;
}

function useAppShellState() {
    const location = useLocation();
    const locationState = location.state as { selectedCountry?: string } | undefined;

    const [selectedCountry, setSelectedCountry] = useState(() => {
        if (typeof window !== 'undefined') {
            const stored = localStorage.getItem('selectedCountry');
            if (stored) return stored;
        }
        return DEFAULT_COUNTRIES[0];
    });

    // Persist selectedCountry to localStorage
    useEffect(() => {
        if (typeof window !== 'undefined') {
            localStorage.setItem('selectedCountry', selectedCountry);
        }
    }, [selectedCountry]);

    // Handle initial country from location state (when navigating from Home)
    useEffect(() => {
        if (locationState?.selectedCountry) {
            setSelectedCountry(locationState.selectedCountry);
        }
    }, [locationState?.selectedCountry]);

    const [authModal, setAuthModal] = useState<AuthModalState>({ isOpen: false, mode: 'login' });
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
        if (typeof window === 'undefined') {
            return true;
        }

        if (window.innerWidth < 1024) {
            return false;
        }

        const saved = sessionStorage.getItem('sidebarCollapsed');
        return saved !== null ? saved === 'true' : true;
    });
    const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
    const [isCreateCaseModalOpen, setIsCreateCaseModalOpen] = useState(false);
    const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
    const [showCaseSuccessModal, setShowCaseSuccessModal] = useState(false);
    const [isFAQModalOpen, setIsFAQModalOpen] = useState(false);
    const [sidebarRefreshTrigger, setSidebarRefreshTrigger] = useState(0);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return undefined;
        }

        sessionStorage.setItem('sidebarCollapsed', String(isSidebarCollapsed));
        return undefined;
    }, [isSidebarCollapsed]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return undefined;
        }

        const enforceMobileCollapsedState = () => {
            if (window.innerWidth < 1024) {
                setIsSidebarCollapsed(false);
            }
        };

        enforceMobileCollapsedState();
        window.addEventListener('resize', enforceMobileCollapsedState);

        return () => {
            window.removeEventListener('resize', enforceMobileCollapsedState);
        };
    }, []);

    const checkAuthStatus = useCallback(() => {
        const accessToken = Cookies.get('p-ai-accessToken');
        setIsLoggedIn(!!accessToken);
    }, []);

    // Extract accessToken from URL (redirect back from podus auth)
    useEffect(() => {
        if (typeof window === 'undefined') return;

        const url = new URL(window.location.href);
        const token = url.searchParams.get('accessToken');

        if (token) {
            // Store token in cookies
            Cookies.set('p-ai-accessToken', token, { expires: 365 * 100 });

            const uid = url.searchParams.get('uid');
            if (uid) {
                Cookies.set('p-ai-uid', uid, { expires: 365 * 100 });
            }

            // Clean the URL (remove auth params)
            url.searchParams.delete('accessToken');
            url.searchParams.delete('uid');
            window.history.replaceState({}, '', url.pathname + url.search + url.hash);

            // Update auth state
            setIsLoggedIn(true);
        }
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return undefined;
        }

        checkAuthStatus();

        const handleStorageChange = () => {
            checkAuthStatus();
        };

        window.addEventListener('storage', handleStorageChange);
        const interval = window.setInterval(checkAuthStatus, 1000);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.clearInterval(interval);
        };
    }, [checkAuthStatus]);

    const openAuthModal = useCallback((mode: AuthModalMode) => {
        // Redirect to podus auth instead of opening a modal
        const currentUrl = window.location.href;
        const redirectParam = encodeURIComponent(currentUrl);
        if (mode === 'signup') {
            window.location.href = `${AUTH_BASE_URL}/sign-up?redirect=${redirectParam}`;
        } else {
            window.location.href = `${AUTH_BASE_URL}/sign-in?redirect=${redirectParam}`;
        }
    }, []);

    const closeAuthModal = useCallback(() => {
        setAuthModal((prev) => ({ ...prev, isOpen: false }));
    }, []);

    const changeAuthModalMode = useCallback((mode: AuthModalMode) => {
        setAuthModal((prev) => ({ ...prev, mode }));
    }, []);

    const toggleSidebar = useCallback(() => {
        setIsSidebarOpen((prev) => !prev);
    }, []);

    const toggleSidebarCollapse = useCallback(() => {
        if (typeof window !== 'undefined' && window.innerWidth < 1024) {
            setIsSidebarCollapsed(false);
            return;
        }

        setIsSidebarCollapsed((prev) => !prev);
    }, []);

    const closeAllSecondaryModals = useCallback(() => {
        setIsHelpModalOpen(false);
        setIsCreateCaseModalOpen(false);
        setIsSettingsModalOpen(false);
        setIsFAQModalOpen(false);
    }, []);

    const openHelpModal = useCallback(() => {
        closeAllSecondaryModals();
        setIsHelpModalOpen(true);
    }, [closeAllSecondaryModals]);

    const openCreateCaseModal = useCallback(() => {
        closeAllSecondaryModals();
        setIsCreateCaseModalOpen(true);
    }, [closeAllSecondaryModals]);

    const openSettingsModal = useCallback(() => {
        closeAllSecondaryModals();
        setIsSettingsModalOpen(true);
    }, [closeAllSecondaryModals]);

    const openFAQModal = useCallback(() => {
        closeAllSecondaryModals();
        setIsFAQModalOpen(true);
    }, [closeAllSecondaryModals]);

    const openCaseSuccessModal = useCallback(() => {
        setShowCaseSuccessModal(true);
    }, []);

    const closeCaseSuccessModal = useCallback(() => {
        setShowCaseSuccessModal(false);
    }, []);

    const triggerSidebarRefresh = useCallback(() => {
        setSidebarRefreshTrigger((prev) => prev + 1);
    }, []);

    return {
        countries: DEFAULT_COUNTRIES,
        selectedCountry,
        setSelectedCountry,
        authModal,
        openAuthModal,
        closeAuthModal,
        changeAuthModalMode,
        isLoggedIn,
        setIsLoggedIn,
        isSidebarOpen,
        setIsSidebarOpen,
        toggleSidebar,
        isSidebarCollapsed,
        toggleSidebarCollapse,
        isHelpModalOpen,
        openHelpModal,
        closeHelpModal: () => setIsHelpModalOpen(false),
        isCreateCaseModalOpen,
        openCreateCaseModal,
        closeCreateCaseModal: () => setIsCreateCaseModalOpen(false),
        isSettingsModalOpen,
        openSettingsModal,
        closeSettingsModal: () => setIsSettingsModalOpen(false),
        isFAQModalOpen,
        openFAQModal,
        closeFAQModal: () => setIsFAQModalOpen(false),
        showCaseSuccessModal,
        openCaseSuccessModal,
        closeCaseSuccessModal,
        sidebarRefreshTrigger,
        triggerSidebarRefresh,
    };
}

const defaultOnLoginSuccess = () => undefined;

export default function AppShell({
    children,
    disableSidebar = false,
    contentClassName = '',
    contentId,
    onLoginSuccess = defaultOnLoginSuccess,
}: AppShellProps) {
    const state = useAppShellState();

    const contextValue = useMemo<AppShellContextValue>(
        () => ({
            countries: state.countries,
            selectedCountry: state.selectedCountry,
            setSelectedCountry: state.setSelectedCountry,
            isLoggedIn: state.isLoggedIn,
            setIsLoggedIn: state.setIsLoggedIn,
            authModal: state.authModal,
            openAuthModal: state.openAuthModal,
            closeAuthModal: state.closeAuthModal,
            changeAuthModalMode: state.changeAuthModalMode,
            isSidebarOpen: state.isSidebarOpen,
            setIsSidebarOpen: state.setIsSidebarOpen,
            toggleSidebar: state.toggleSidebar,
            isSidebarCollapsed: state.isSidebarCollapsed,
            toggleSidebarCollapse: state.toggleSidebarCollapse,
            isHelpModalOpen: state.isHelpModalOpen,
            openHelpModal: state.openHelpModal,
            closeHelpModal: state.closeHelpModal,
            isCreateCaseModalOpen: state.isCreateCaseModalOpen,
            openCreateCaseModal: state.openCreateCaseModal,
            closeCreateCaseModal: state.closeCreateCaseModal,
            isSettingsModalOpen: state.isSettingsModalOpen,
            openSettingsModal: state.openSettingsModal,
            closeSettingsModal: state.closeSettingsModal,
            isFAQModalOpen: state.isFAQModalOpen,
            openFAQModal: state.openFAQModal,
            closeFAQModal: state.closeFAQModal,
            showCaseSuccessModal: state.showCaseSuccessModal,
            openCaseSuccessModal: state.openCaseSuccessModal,
            closeCaseSuccessModal: state.closeCaseSuccessModal,
            sidebarRefreshTrigger: state.sidebarRefreshTrigger,
            triggerSidebarRefresh: state.triggerSidebarRefresh,
        }),
        [state],
    );

    return (
        <AppShellContext.Provider value={contextValue}>
            <div
                className="bg-background text-secondary flex overflow-hidden ios-safe-area-top w-full max-w-full"
                style={{ height: '100dvh' }}
            >
                {!disableSidebar && state.isLoggedIn && (
                    <ChatSidebar
                        isOpen={state.isSidebarOpen}
                        onToggle={state.toggleSidebar}
                        isCollapsed={state.isSidebarCollapsed}
                        onToggleCollapse={state.toggleSidebarCollapse}
                        onOpenHelpModal={state.openHelpModal}
                        onOpenCreateCaseModal={state.openCreateCaseModal}
                        onOpenSettingsModal={state.openSettingsModal}
                    />
                )}

                <Container id={contentId} className={`bg-background flex-1 overflow-hidden ${contentClassName}`}>
                    <div className="pointer-events-none absolute inset-0">
                        <img
                            src="/assets/background-skeleton.svg"
                            alt="Perspective grid background"
                            className="absolute bottom-0 left-1/2 w-[140%] -translate-x-1/2 select-none"
                        />
                    </div>
                    {children}
                </Container>
            </div>

            <HelpSupportModal isOpen={state.isHelpModalOpen} onClose={state.closeHelpModal} />

            <CreateCaseModal
                isOpen={state.isCreateCaseModalOpen}
                onClose={state.closeCreateCaseModal}
                onSuccess={state.openCaseSuccessModal}
            />

            <SettingsModal isOpen={state.isSettingsModalOpen} onClose={state.closeSettingsModal} />

            <SuccessModal
                isOpen={state.showCaseSuccessModal}
                onClose={state.closeCaseSuccessModal}
                title="Case Created Successfully!"
                message="Your case has been submitted and will be reviewed by a lawyer."
            />

            <FAQModal isOpen={state.isFAQModalOpen} onClose={state.closeFAQModal} />
        </AppShellContext.Provider>
    );
}
